const DAY = 86_400_000

const weekday = new Intl.DateTimeFormat('da-DK', { weekday: 'long' })
const shortDate = new Intl.DateTimeFormat('da-DK', { day: 'numeric', month: 'short' })
const monthShort = new Intl.DateTimeFormat('da-DK', { month: 'short' })
const longDate = new Intl.DateTimeFormat('da-DK', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
})
const timeFormat = new Intl.DateTimeFormat('da-DK', { hour: '2-digit', minute: '2-digit' })
const krFormat = new Intl.NumberFormat('da-DK', { maximumFractionDigits: 0 })

/** "2026-10-08" tolkes som lokal dato — ikke UTC. */
export function parseDate(value: string): Date | null {
  if (!value) return null
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value)
  return Number.isNaN(date.getTime()) ? null : date
}

function startOfDay(date: Date): number {
  const copy = new Date(date)
  copy.setHours(0, 0, 0, 0)
  return copy.getTime()
}

/** Antal hele dage fra i dag til datoen (negativ = fortid). */
export function dayDiff(value: string): number | null {
  const date = parseDate(value)
  if (!date) return null
  return Math.round((startOfDay(date) - startOfDay(new Date())) / DAY)
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/** Kommende dato: "I dag", "I morgen", "Onsdag" eller "12. okt." */
export function relativeUpcoming(value: string): string {
  const diff = dayDiff(value)
  const date = parseDate(value)
  if (diff === null || !date) return 'Ikke planlagt'
  if (diff === 0) return 'I dag'
  if (diff === 1) return 'I morgen'
  if (diff === -1) return 'I går'
  if (diff > 1 && diff < 7) return capitalize(weekday.format(date))
  return shortDate.format(date)
}

/** Fortid: "I dag", "I går", "3 dage siden" eller "12. okt." */
export function relativeAgo(value: string): string {
  const diff = dayDiff(value)
  const date = parseDate(value)
  if (diff === null || !date) return ''
  if (diff >= 0) return 'I dag'
  if (diff === -1) return 'I går'
  if (diff > -14) return `${-diff} dage siden`
  return shortDate.format(date)
}

export function dayAndMonth(value: string): { day: string; month: string } | null {
  const date = parseDate(value)
  if (!date) return null
  return { day: String(date.getDate()), month: monthShort.format(date).replace('.', '') + '.' }
}

/** Klokkeslæt, hvis datoen indeholder et (fx "2026-10-08T08:00"). */
export function timeOf(value: string): string {
  if (!value.includes('T')) return ''
  const date = parseDate(value)
  return date ? timeFormat.format(date) : ''
}

export function todayLong(): string {
  return capitalize(longDate.format(new Date())).replace(/^(\w+) (\d)/u, '$1 d. $2')
}

export function greeting(date = new Date()): string {
  const hour = date.getHours()
  if (hour >= 5 && hour < 10) return 'Godmorgen'
  if (hour >= 10 && hour < 12) return 'God formiddag'
  if (hour >= 12 && hour < 18) return 'God eftermiddag'
  return 'Godaften'
}

export function formatKr(amount: number): string {
  return `${krFormat.format(Math.round(amount || 0))} kr.`
}

/** "Enghavevej 6, 8500 Grenaa" → "Grenaa" */
export function cityFromAddress(address: string): string {
  if (!address) return ''
  const parts = address.split(',').map((part) => part.trim()).filter(Boolean)
  const last = parts[parts.length - 1] ?? ''
  return last.replace(/^\d{4}\s+/, '') || last
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  const first = parts[0][0] ?? ''
  const last = parts.length > 1 ? parts[parts.length - 1][0] ?? '' : ''
  return (first + last).toUpperCase()
}
